import type {
  SessionValidity,
  TypingSessionSummary,
} from "../measurement";
import type {
  KeyValueStorage,
  RepositoryInitialization,
  SessionContext,
  SessionStoreDocument,
  StoredTypingSession,
} from "./types";
import type { SkillMastery } from "../adaptive";

export const SESSION_STORE_KEY = "masterTyping:session-store:v2";
export const LEGACY_STATS_KEY = "masterTypingStats";
export const LEGACY_BACKUP_KEY = "masterTyping:migration-backup:v1";
export const CORRUPTION_BACKUP_PREFIX = "masterTyping:corrupt-backup";
export const MAX_STORED_SESSIONS = 2_000;

interface LegacyHistoryEntry {
  date?: unknown;
  wpm?: unknown;
  accuracy?: unknown;
  consistency?: unknown;
  time?: unknown;
}

interface LegacyStatistics {
  pro?: { history?: unknown };
  assessment?: { history?: unknown };
}

function finiteNumber(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function boundedPercentage(value: unknown): number {
  return Math.min(100, Math.max(0, finiteNumber(value)));
}

function safeIsoDate(value: unknown, fallback: string): string {
  if (typeof value !== "string") {
    return fallback;
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? fallback : new Date(timestamp).toISOString();
}

function legacyValidity(reasons: string[]): SessionValidity {
  return {
    isValidPractice: false,
    isValidBenchmark: false,
    isVerified: false,
    reasons,
    pasteDetected: false,
    timingDiscontinuityDetected: false,
    minimumDurationMet: false,
    minimumAttemptsMet: false,
    plausibleSpeed: true,
  };
}

function buildLegacySummary(
  entry: LegacyHistoryEntry,
  sessionType: "practice" | "assessment",
  index: number,
  nowIso: string,
): TypingSessionSummary {
  const startedAt = safeIsoDate(entry.date, nowIso);
  const accurateWpm = Math.max(0, finiteNumber(entry.wpm));
  const accuracy = boundedPercentage(entry.accuracy);
  const activeDurationMs =
    sessionType === "practice"
      ? Math.max(0, finiteNumber(entry.time) * 1_000)
      : 0;
  const estimatedCorrectCharacters =
    activeDurationMs > 0
      ? Math.round((accurateWpm * 5 * activeDurationMs) / 60_000)
      : 0;
  const estimatedAttempts =
    accuracy > 0
      ? Math.round(estimatedCorrectCharacters / (accuracy / 100))
      : estimatedCorrectCharacters;
  const reasons = [
    "legacy-import-unverified",
    "raw-keystrokes-unavailable",
  ];

  if (activeDurationMs <= 0) {
    reasons.push("active-duration-unavailable");
  }

  return {
    id: `legacy-${sessionType}-${startedAt.replace(/\W/g, "")}-${index}`,
    schemaVersion: 1,
    sessionType,
    startedAt,
    completedAt: startedAt,
    pausedDurationMs: 0,
    excludedIdleDurationMs: 0,
    activeDurationMs,
    sourceLength: estimatedAttempts,
    finalTypedLength: estimatedAttempts,
    printableAttempts: estimatedAttempts,
    correctPrintableAttempts: estimatedCorrectCharacters,
    initialErrors: Math.max(0, estimatedAttempts - estimatedCorrectCharacters),
    correctedInitialErrors: 0,
    uncorrectedErrors: Math.max(
      0,
      estimatedAttempts - estimatedCorrectCharacters,
    ),
    rawWpm: accurateWpm,
    accurateWpm,
    netWpm: accurateWpm,
    keystrokeAccuracy: accuracy,
    finalTextAccuracy: accuracy,
    consistency:
      sessionType === "assessment"
        ? boundedPercentage(entry.consistency)
        : null,
    isProvisional: true,
    validity: legacyValidity(reasons),
    completed: true,
    measurementVersion: "2.0.0",
  };
}

function isSessionStoreDocument(value: unknown): value is SessionStoreDocument {
  if (!value || typeof value !== "object") {
    return false;
  }
  const candidate = value as Partial<SessionStoreDocument>;
  return (
    candidate.schemaVersion === 2 &&
    typeof candidate.revision === "number" &&
    Array.isArray(candidate.sessions)
  );
}

export class SessionRepository {
  private document: SessionStoreDocument | null = null;

  constructor(
    private readonly storage: KeyValueStorage,
    private readonly now: () => Date = () => new Date(),
  ) {}

  initialize(): RepositoryInitialization {
    const warnings: string[] = [];
    let recoveredFromCorruption = false;
    const existingRaw = this.storage.getItem(SESSION_STORE_KEY);

    if (existingRaw !== null) {
      try {
        const parsed: unknown = JSON.parse(existingRaw);
        if (isSessionStoreDocument(parsed)) {
          parsed.mastery = Array.isArray(parsed.mastery) ? parsed.mastery : [];
          parsed.processedMasterySessionIds = Array.isArray(
            parsed.processedMasterySessionIds,
          )
            ? parsed.processedMasterySessionIds
            : [];
          this.document = parsed;
          return {
            document: this.snapshot(),
            recoveredFromCorruption,
            migratedLegacyData: false,
            warnings,
          };
        }
        warnings.push("The existing session store had an unsupported shape.");
      } catch {
        warnings.push("The existing session store contained invalid JSON.");
      }

      recoveredFromCorruption = true;
      this.backUpCorruptValue(existingRaw);
    }

    this.document = this.createEmptyDocument();
    const migratedLegacyData = this.migrateLegacyStatistics(warnings);
    this.persist();

    return {
      document: this.snapshot(),
      recoveredFromCorruption,
      migratedLegacyData,
      warnings,
    };
  }

  append(
    summary: TypingSessionSummary,
    context: SessionContext,
  ): StoredTypingSession {
    this.ensureInitialized();
    const record = {
      summary: structuredClone(summary),
      context: structuredClone(context),
    };

    const withoutDuplicate = this.document!.sessions.filter(
      (session) => session.summary.id !== summary.id,
    );
    this.document!.sessions = [...withoutDuplicate, record].slice(
      -MAX_STORED_SESSIONS,
    );
    this.document!.revision += 1;
    this.document!.updatedAt = this.now().toISOString();
    this.persist();
    return structuredClone(record);
  }

  list(): StoredTypingSession[] {
    this.ensureInitialized();
    return structuredClone(this.document!.sessions);
  }

  getById(id: string): StoredTypingSession | null {
    this.ensureInitialized();
    const result = this.document!.sessions.find(
      (session) => session.summary.id === id,
    );
    return result ? structuredClone(result) : null;
  }

  clearSessions(): void {
    this.ensureInitialized();
    this.document!.sessions = [];
    this.document!.revision += 1;
    this.document!.updatedAt = this.now().toISOString();
    this.persist();
  }

  replaceDocument(document: SessionStoreDocument): SessionStoreDocument {
    if (document.schemaVersion !== 2) {
      throw new Error("Unsupported session-store schema version.");
    }

    this.document = structuredClone(document);
    this.persist();
    return this.snapshot();
  }

  reset(): SessionStoreDocument {
    this.document = this.createEmptyDocument();
    this.persist();
    return this.snapshot();
  }

  listMastery(): SkillMastery[] {
    this.ensureInitialized();
    return structuredClone(this.document!.mastery);
  }

  hasProcessedMasterySession(sessionId: string): boolean {
    this.ensureInitialized();
    return this.document!.processedMasterySessionIds.includes(sessionId);
  }

  saveMasteryForSession(
    sessionId: string,
    mastery: readonly SkillMastery[],
  ): boolean {
    this.ensureInitialized();
    if (this.document!.processedMasterySessionIds.includes(sessionId)) {
      return false;
    }

    this.document!.mastery = structuredClone([...mastery]);
    this.document!.processedMasterySessionIds = [
      ...this.document!.processedMasterySessionIds,
      sessionId,
    ].slice(-MAX_STORED_SESSIONS);
    this.document!.revision += 1;
    this.document!.updatedAt = this.now().toISOString();
    this.persist();
    return true;
  }

  snapshot(): SessionStoreDocument {
    this.ensureInitialized();
    return structuredClone(this.document!);
  }

  private createEmptyDocument(): SessionStoreDocument {
    const nowIso = this.now().toISOString();
    return {
      schemaVersion: 2,
      revision: 0,
      createdAt: nowIso,
      updatedAt: nowIso,
      sessions: [],
      mastery: [],
      processedMasterySessionIds: [],
      migration: null,
    };
  }

  private migrateLegacyStatistics(warnings: string[]): boolean {
    const legacyRaw = this.storage.getItem(LEGACY_STATS_KEY);
    if (legacyRaw === null) {
      return false;
    }

    this.storage.setItem(LEGACY_BACKUP_KEY, legacyRaw);

    let parsed: LegacyStatistics;
    try {
      parsed = JSON.parse(legacyRaw) as LegacyStatistics;
    } catch {
      warnings.push("Legacy statistics could not be parsed; a backup was kept.");
      this.document!.migration = {
        sourceVersion: 1,
        completedAt: this.now().toISOString(),
        importedSessionCount: 0,
        backupKey: LEGACY_BACKUP_KEY,
        warnings: [...warnings],
      };
      return true;
    }

    const nowIso = this.now().toISOString();
    const proHistory = Array.isArray(parsed.pro?.history)
      ? (parsed.pro.history as LegacyHistoryEntry[])
      : [];
    const assessmentHistory = Array.isArray(parsed.assessment?.history)
      ? (parsed.assessment.history as LegacyHistoryEntry[])
      : [];
    const imported: StoredTypingSession[] = [
      ...proHistory.map((entry, index) => ({
        summary: buildLegacySummary(entry, "practice", index, nowIso),
        context: {
          mode: "legacy" as const,
          legacySource: "pro" as const,
        },
      })),
      ...assessmentHistory.map((entry, index) => ({
        summary: buildLegacySummary(entry, "assessment", index, nowIso),
        context: {
          mode: "legacy" as const,
          legacySource: "assessment" as const,
        },
      })),
    ];

    this.document!.sessions = imported.slice(-MAX_STORED_SESSIONS);
    this.document!.migration = {
      sourceVersion: 1,
      completedAt: nowIso,
      importedSessionCount: imported.length,
      backupKey: LEGACY_BACKUP_KEY,
      warnings: [...warnings],
    };
    return true;
  }

  private backUpCorruptValue(value: string): void {
    const suffix = this.now().toISOString().replace(/\W/g, "");
    this.storage.setItem(`${CORRUPTION_BACKUP_PREFIX}:${suffix}`, value);
  }

  private ensureInitialized(): void {
    if (this.document === null) {
      this.initialize();
    }
  }

  private persist(): void {
    this.ensureInitialized();
    this.storage.setItem(SESSION_STORE_KEY, JSON.stringify(this.document));
  }
}
